import {
  GridColDef,
  gridColumnFieldsSelector,
  GridColumnResizeParams,
} from '@mui/x-data-grid-premium'
import { GridApiPro } from '@mui/x-data-grid-pro/models/gridApiPro'
import React from 'react'

const useColumnsState = (
  apiRef: React.RefObject<GridApiPro | null>,
  columns: GridColDef[],
  orderedFieldsOverride?: GridColDef['field'][],
) => {
  const columnFields = React.useMemo(
    () => columns.map((column) => column.field),
    [columns],
  )
  const columnFieldSet = React.useMemo(
    () => new Set<GridColDef['field']>(columnFields),
    [columnFields],
  )
  const columnsByField = React.useMemo(
    () => new Map(columns.map((column) => [column.field, column])),
    [columns],
  )

  const [widths, setWidths] = React.useState<
    Record<GridColDef['field'], number>
  >({})

  // Initialize orderedFields only once with the initial column fields
  const [orderedFields, setOrderedFields] = React.useState<
    GridColDef['field'][]
  >(() => columnFields)

  // Update orderedFields when columns change (but only if the fields actually changed)
  React.useEffect(() => {
    // Only update if the column set has changed (added/removed columns)
    setOrderedFields((currentFields) => {
      const fieldsChanged =
        columnFields.length !== currentFields.length ||
        currentFields.some((field) => !columnFieldSet.has(field))

      if (fieldsChanged) {
        const currentFieldsSet = new Set(currentFields)
        // Preserve the order of existing fields and append new ones
        const orderedExisting = currentFields.filter((field) =>
          columnFieldSet.has(field),
        )
        const newFieldsToAdd = columnFields.filter(
          (field) => !currentFieldsSet.has(field),
        )
        return [...orderedExisting, ...newFieldsToAdd]
      }

      return currentFields
    })
  }, [columnFields, columnFieldSet])

  // Apply externally provided order (e.g. saved views) while preserving unknown/new fields.
  React.useEffect(() => {
    if (!orderedFieldsOverride || orderedFieldsOverride.length === 0) return

    const validOverride = orderedFieldsOverride.filter((field) =>
      columnFieldSet.has(field),
    )

    setOrderedFields((currentFields) => {
      const overrideSet = new Set(validOverride)
      const remainingCurrent = currentFields.filter(
        (field) =>
          !overrideSet.has(field) && columnFieldSet.has(field),
      )
      const missingFromCurrent = columnFields.filter(
        (field) => !overrideSet.has(field) && !remainingCurrent.includes(field),
      )

      const nextFields = [...validOverride, ...remainingCurrent, ...missingFromCurrent]
      const unchanged =
        nextFields.length === currentFields.length &&
        nextFields.every((field, index) => field === currentFields[index])

      return unchanged ? currentFields : nextFields
    })
  }, [orderedFieldsOverride, columnFields, columnFieldSet])

  const hadOrderedFieldsOverrideRef = React.useRef<boolean>(
    !!orderedFieldsOverride && orderedFieldsOverride.length > 0,
  )

  // If we previously had a saved-order override and it is cleared,
  // reset back to natural column definition order.
  React.useEffect(() => {
    const hasOverride = !!orderedFieldsOverride && orderedFieldsOverride.length > 0
    if (!hasOverride && hadOrderedFieldsOverrideRef.current) {
      setOrderedFields(columnFields)
    }

    hadOrderedFieldsOverrideRef.current = hasOverride
  }, [orderedFieldsOverride, columnFields])

  const onColumnWidthChange = React.useCallback(
    ({ colDef, width }: GridColumnResizeParams) => {
      setWidths((prev) => ({ ...prev, [colDef.field]: width }))
    },
    [],
  )

  const onColumnOrderChange = React.useCallback(() => {
    if (!apiRef.current) {
      return
    }
    const newFields = gridColumnFieldsSelector({ current: apiRef.current })
    setOrderedFields(newFields)
  }, [apiRef])

  const resetColumnOrder = React.useCallback(() => {
    setOrderedFields(columnFields)
  }, [columnFields])

  const computedColumns = React.useMemo(
    () =>
      orderedFields.reduce<GridColDef[]>((acc, field) => {
        const column = columnsByField.get(field)
        if (!column) {
          return acc
        }

        // Apply saved width if it exists
        if (widths[field] !== undefined) {
          acc.push({
            ...column,
            // see https://github.com/mui/mui-x/issues/4310#issuecomment-1082714528
            // handles flex for initial widths, and then removes it
            flex: undefined,
            width: widths[field],
          })
        } else {
          acc.push(column)
        }
        return acc
      }, []),
    [columnsByField, widths, orderedFields],
  )

  // Memoize the return value to prevent unnecessary re-renders
  return React.useMemo(
    () => ({
      columns: computedColumns,
      onColumnWidthChange,
      onColumnOrderChange,
      resetColumnOrder,
    }),
    [computedColumns, onColumnWidthChange, onColumnOrderChange, resetColumnOrder],
  )
}

export default useColumnsState
